import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

// ─────────────────── Primitives ───────────────────

export const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};
export const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

// Animated radial gradient blobs. `muted` dims the opacity. Place inside a `position:relative` parent.
export function AuroraBlobs({ muted, palette = ['rgba(176,138,62,0.55)', 'rgba(122,31,43,0.7)', 'rgba(30,58,95,0.55)'] }) {
  const reduce = useReducedMotion();
  const op = muted ? 0.35 : 0.65;
  return (
    <>
      <motion.div
        aria-hidden
        animate={reduce ? {} : { x: [0, 60, -30, 0], y: [0, -40, 30, 0] }}
        transition={{ duration: 24, repeat: Infinity, ease: 'easeInOut' }}
        style={{ position: 'absolute', top: '-10%', left: '-10%', width: 520, height: 520, borderRadius: '50%', background: `radial-gradient(circle, ${palette[0]} 0%, transparent 70%)`, filter: 'blur(80px)', opacity: op, pointerEvents: 'none' }}
      />
      <motion.div
        aria-hidden
        animate={reduce ? {} : { x: [0, -50, 40, 0], y: [0, 30, -20, 0] }}
        transition={{ duration: 28, repeat: Infinity, ease: 'easeInOut' }}
        style={{ position: 'absolute', top: '20%', right: '-10%', width: 480, height: 480, borderRadius: '50%', background: `radial-gradient(circle, ${palette[1]} 0%, transparent 70%)`, filter: 'blur(90px)', opacity: op, pointerEvents: 'none' }}
      />
      <motion.div
        aria-hidden
        animate={reduce ? {} : { x: [0, 40, -20, 0], y: [0, -20, 40, 0] }}
        transition={{ duration: 32, repeat: Infinity, ease: 'easeInOut' }}
        style={{ position: 'absolute', bottom: '-15%', left: '30%', width: 460, height: 460, borderRadius: '50%', background: `radial-gradient(circle, ${palette[2]} 0%, transparent 70%)`, filter: 'blur(100px)', opacity: op, pointerEvents: 'none' }}
      />
    </>
  );
}

// SVG-noise overlay for film-grain texture. Use inside dark surfaces.
export function GrainOverlay({ opacity = 0.06 }) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute', inset: 0, opacity, pointerEvents: 'none', mixBlendMode: 'overlay',
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='3'/></filter><rect width='200' height='200' filter='url(%23n)' opacity='0.6'/></svg>\")",
        backgroundSize: '200px 200px',
      }}
    />
  );
}

// Scroll-revealed wrapper. Children fade-up once when entering viewport.
export function Reveal({ children, delay = 0, margin = '-60px', y = 24, style }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
      style={style}
    >
      {children}
    </motion.div>
  );
}

// Pill-shaped glass chip used for kicker/eyebrow lines.
export function GlassChip({ children, dot = 'var(--gold)', tone = 'dark', style }) {
  const palette = tone === 'dark'
    ? { bg: 'rgba(255,255,255,0.06)', border: 'rgba(237,227,209,0.14)', text: 'var(--gold)' }
    : { bg: 'rgba(122,31,43,0.06)', border: 'rgba(122,31,43,0.18)', text: 'var(--primary)' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 10,
      padding: '8px 14px', borderRadius: 999,
      background: palette.bg, border: `1px solid ${palette.border}`,
      backdropFilter: 'blur(12px) saturate(180%)',
      WebkitBackdropFilter: 'blur(12px) saturate(180%)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
      ...style,
    }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, boxShadow: tone === 'dark' ? `0 0 12px ${dot}` : 'none' }} />}
      <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: palette.text }}>{children}</span>
    </span>
  );
}

// Frosted glass card with subtle inner highlight. Use for surfaces on light backgrounds.
export function GlassCard({ children, style, hover = true }) {
  return (
    <motion.div
      whileHover={hover ? { y: -4, transition: { duration: 0.2 } } : undefined}
      style={{
        background: 'rgba(255,255,255,0.55)',
        border: '1px solid rgba(229,219,200,0.6)',
        borderRadius: 20,
        backdropFilter: 'blur(20px) saturate(160%)',
        WebkitBackdropFilter: 'blur(20px) saturate(160%)',
        boxShadow: '0 8px 24px rgba(42,31,26,0.05), 0 1px 0 rgba(255,255,255,0.7) inset',
        ...style,
      }}
    >
      {children}
    </motion.div>
  );
}

// Glass-pill link for dark backgrounds.
export function GlassLink({ children, onClick, href, to, style }) {
  const Comp = href ? 'a' : to ? RouterLink : 'button';
  const props = href ? { href, target: '_blank', rel: 'noreferrer' } : to ? { to } : { onClick };
  return (
    <Comp
      {...props}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start',
        color: '#EDE3D1', textDecoration: 'none', fontSize: 13, fontWeight: 600,
        padding: '10px 18px', borderRadius: 999, border: 'none',
        background: 'rgba(255,255,255,0.06)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        borderColor: 'rgba(237,227,209,0.18)', borderWidth: 1, borderStyle: 'solid',
        letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer',
        ...style,
      }}
    >
      {children}
    </Comp>
  );
}

// Dark hero section with aurora + grain, optional image background.
export function PageHero({ kicker, title, hindi, body, image, height = 360, children, palette }) {
  return (
    <section style={{ position: 'relative', minHeight: height, background: '#1A100E', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
      {image && (
        <img src={image} alt="" aria-hidden style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.35 }} />
      )}
      <AuroraBlobs palette={palette} />
      <GrainOverlay />
      <div style={{ position: 'relative', zIndex: 2, maxWidth: 1280, margin: '0 auto', padding: '64px 24px', width: '100%' }}>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          {kicker && <GlassChip>{kicker}</GlassChip>}
          {hindi && <div className="hindi" style={{ fontSize: 16, color: 'var(--gold)', marginTop: 18, opacity: 0.9 }}>{hindi}</div>}
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 1.05, color: '#FFF', marginTop: 16, marginBottom: body ? 16 : 0, fontWeight: 600, letterSpacing: '-0.02em' }}>
            {title}
          </h1>
          {body && <p style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(237,227,209,0.72)', maxWidth: 540 }}>{body}</p>}
          {children}
        </motion.div>
      </div>
    </section>
  );
}

// Section kicker + serif title + optional Hindi tag + "see all" right link.
export function SectionHeader({ kicker, title, hindi, href, navigate }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.6 }}
      style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}
    >
      <div>
        {kicker && <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 8 }}>{kicker}</div>}
        <h2 className="serif" style={{ fontSize: 'clamp(24px, 3vw, 36px)', color: 'var(--ink)', lineHeight: 1, letterSpacing: '-0.01em' }}>
          {title} {hindi && <span className="hindi" style={{ fontSize: '0.55em', color: 'var(--muted)', marginLeft: 10 }}>{hindi}</span>}
        </h2>
      </div>
      {href && (
        <button onClick={() => navigate?.(href)} className="ve-see-all-btn">
          See all  ›
        </button>
      )}
    </motion.div>
  );
}

// Self-falling-back image. Tries src, swaps to fallback element on error.
export function Img({ src, fallback, alt, style }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return fallback || <div style={{ position: 'absolute', inset: 0, background: 'var(--surface-2)' }} />;
  }
  return (
    <motion.img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      initial={{ opacity: 0, scale: 1.06 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block', ...style }}
    />
  );
}

// Magnetic-feeling CTA pill, supports `to` (router link) or `onClick`.
export function MagneticButton({ to, onClick, variant = 'primary', size = 'lg', children, disabled, type, style }) {
  const sizeStyle = size === 'sm' ? { height: 34, padding: '0 14px', fontSize: 11 } : size === 'md' ? { height: 44, padding: '0 18px', fontSize: 13 } : { height: 52, padding: '0 28px', fontSize: 14 };
  const variantStyle = variantStyles[variant] || variantStyles.primary;
  const inner = (
    <motion.span
      whileHover={disabled ? {} : { scale: 1.03, transition: { duration: 0.2 } }}
      whileTap={disabled ? {} : { scale: 0.97 }}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        borderRadius: 999, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
        textDecoration: 'none', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
        ...sizeStyle, ...variantStyle, ...style,
      }}
    >
      {children}
    </motion.span>
  );
  if (to) return <RouterLink to={to} style={{ textDecoration: 'none' }}>{inner}</RouterLink>;
  return <button type={type} onClick={onClick} disabled={disabled} style={{ background: 'transparent', border: 'none', padding: 0 }}>{inner}</button>;
}

const variantStyles = {
  primary: { background: 'var(--primary)', color: '#FFF', border: 'none', boxShadow: '0 10px 28px rgba(122,31,43,0.25)' },
  gold: { background: 'var(--gold)', color: '#FFF', border: 'none', boxShadow: '0 12px 32px rgba(176,138,62,0.35)' },
  ghost: { background: 'transparent', color: 'var(--ink)', border: '1px solid var(--ink)' },
  glassDark: { background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', color: '#EDE3D1', border: '1px solid rgba(237,227,209,0.3)' },
  outline: { background: 'transparent', color: 'var(--primary)', border: '1px solid var(--primary)' },
};

// Floating breadcrumb pill.
export function Crumbs({ items }) {
  return (
    <nav style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 20 }}>
      {items.map((it, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {it.to ? <RouterLink to={it.to} style={{ color: 'var(--muted)', textDecoration: 'none' }}>{it.label}</RouterLink> : <span style={{ color: 'var(--ink)' }}>{it.label}</span>}
          {i < items.length - 1 && <span style={{ color: 'var(--line)' }}>›</span>}
        </span>
      ))}
    </nav>
  );
}

// Global CSS injector — call once anywhere; idempotent.
export function GlobalUiStyles() {
  return (
    <style>{`
      .ve-see-all-btn {
        font-size: 12px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--primary); border: 1px solid rgba(122,31,43,0.18);
        background: rgba(122,31,43,0.06); border-radius: 999px;
        padding: 8px 14px; cursor: pointer;
        transition: background 0.2s ease, transform 0.2s ease;
      }
      .ve-see-all-btn:hover { background: rgba(122,31,43,0.12); transform: translateY(-1px); }
      .ve-tab {
        border: none; background: transparent; padding: 12px 18px;
        font-size: 12px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
        color: var(--muted); cursor: pointer; position: relative;
        transition: color 0.2s ease;
      }
      .ve-tab:hover { color: var(--ink); }
      .ve-tab.active { color: var(--ink); }
      .ve-tab.active::after {
        content: ''; position: absolute; bottom: -1px; left: 14px; right: 14px;
        height: 2px; background: var(--ink); border-radius: 1px;
      }
      .ve-glass-form-card {
        background: rgba(255,255,255,0.7);
        border: 1px solid rgba(229,219,200,0.65);
        border-radius: 22px;
        backdrop-filter: blur(24px) saturate(160%);
        -webkit-backdrop-filter: blur(24px) saturate(160%);
        box-shadow: 0 24px 60px rgba(42,31,26,0.08), 0 1px 0 rgba(255,255,255,0.8) inset;
      }
      .ve-fld {
        width: 100%; height: 48px; padding: 0 16px;
        background: rgba(255,255,255,0.6); border: 1px solid var(--line);
        border-radius: 12px; font-size: 14px; color: var(--ink); outline: none;
        transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
      }
      .ve-fld:focus { border-color: var(--ink); background: #FFF; box-shadow: 0 0 0 4px rgba(42,31,26,0.06); }
      .ve-fld:disabled { opacity: 0.6; cursor: not-allowed; }
      @keyframes ve-pulse {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 0.8; }
      }
      .ve-skeleton { animation: ve-pulse 1.5s ease infinite; background: var(--surface-2); border-radius: 12px; }
    `}</style>
  );
}
